import { apiErrorMessage, apiErrorStatus } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { useActiveDocument } from '@/hooks/useActiveDocument';
import { useDetectionPolling } from '@/hooks/useDetectionPolling';
import { ACTIVE_JOB_STATUSES, type DetectionStatusModel, type DocumentModel } from '@/types/documents.types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * Which block the upload route shows.
 *
 * `checking` — a remembered document is being looked up; `idle` — nothing uploaded yet;
 * `ready` — a document is on the server and detection is not running; `detecting` — a run is in
 * progress and being polled.
 */
export type UploadPhase = 'checking' | 'idle' | 'ready' | 'detecting';

/** How the last detection run of the current document ended, for the note on the document card. */
export type DetectionOutcome = { kind: 'cancelled' } | { kind: 'failed'; message: string };

export interface UploadFlow {
  phase: UploadPhase;
  document: DocumentModel | null;
  /** Status of the run in progress; `null` until the first poll answers. */
  detection: DetectionStatusModel | null;
  /** A transient polling failure, shown under the step list. */
  pollingError: string | null;
  outcome: DetectionOutcome | null;
  /** Failure of the last action taken on this screen (lookup, start, discard). */
  error: string | null;
  isStarting: boolean;
  isDiscarding: boolean;
  onUploaded: (document: DocumentModel) => void;
  startDetection: () => Promise<void>;
  discard: () => Promise<void>;
}

const reviewPath = (documentId: string): string => `/documents/${documentId}`;

const isActiveJob = (detection: DetectionStatusModel | null | undefined): boolean =>
  detection != null && ACTIVE_JOB_STATUSES.includes(detection.status);

const outcomeOf = (detection: DetectionStatusModel | null | undefined, fallback: string): DetectionOutcome | null => {
  if (detection?.status === 'cancelled') return { kind: 'cancelled' };
  if (detection?.status === 'failed') return { kind: 'failed', message: detection.error ?? fallback };
  return null;
};

/**
 * The state machine behind the upload route: arrival check, upload, detection, cancel, expiry.
 *
 * The backend keeps a document for two idle hours and `useActiveDocument` remembers its id, so
 * arriving at the start page first asks whether that document still exists: a running detection
 * resumes the progress view, a finished one is dropped from the server, and a 404 quietly clears
 * the id. Every 404 anywhere in the flow means the same thing and is handled the same way — the
 * session is gone, start over.
 */
export const useUploadFlow = (): UploadFlow => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useActiveDocument();
  const [phase, setPhase] = useState<UploadPhase>(() => (activeId ? 'checking' : 'idle'));
  const [document, setDocument] = useState<DocumentModel | null>(null);
  const [outcome, setOutcome] = useState<DetectionOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const polling = useDetectionPolling(document?.id ?? null, phase === 'detecting');

  const expire = useCallback(() => {
    setActiveId(null);
    setDocument(null);
    setOutcome(null);
    setError(null);
    setPhase('idle');
  }, [setActiveId]);

  // Best effort: a failed delete only means the backend's idle expiry removes the document later.
  const dropReviewed = useCallback(
    (documentId: string) => {
      setActiveId(null);
      setPhase('idle');
      documentsApi.delete(documentId).catch(() => undefined);
    },
    [setActiveId]
  );

  // Arrival: look the remembered document up once. `phase` leaves `checking` for good afterwards,
  // so later changes of `activeId` (our own writes) never trigger a second lookup.
  useEffect(() => {
    if (phase !== 'checking') return;
    if (!activeId) {
      setPhase('idle');
      return;
    }
    let cancelled = false;
    documentsApi
      .get(activeId)
      .then((found) => {
        if (cancelled) return;
        // A reviewed document is not kept: the user who comes back to the start page is done with
        // it, so it is dropped from the server's memory instead of waiting for its idle expiry.
        if (found.detection?.status === 'done') {
          dropReviewed(found.id);
          return;
        }
        setDocument(found);
        setOutcome(outcomeOf(found.detection, t('documents.card.errors.detectionFailed')));
        setPhase(isActiveJob(found.detection) ? 'detecting' : 'ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (apiErrorStatus(err) === 404) {
          expire();
          return;
        }
        setError(apiErrorMessage(err, t('common.requestFailed')));
        setPhase('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [phase, activeId, expire, dropReviewed, t]);

  // The run ended: a finished one opens the review, the other two ends return to the card with a
  // note. `polling.status` is `null` whenever the phase is not `detecting`, so this never fires
  // on a stale status.
  useEffect(() => {
    const status = polling.status;
    if (!status || !document) return;
    if (status.status === 'done') {
      navigate(reviewPath(document.id));
      return;
    }
    const ended = outcomeOf(status, t('documents.card.errors.detectionFailed'));
    if (ended) {
      setOutcome(ended);
      setPhase('ready');
    }
  }, [polling.status, document, navigate, t]);

  useEffect(() => {
    if (polling.isExpired) expire();
  }, [polling.isExpired, expire]);

  const onUploaded = useCallback(
    (uploaded: DocumentModel) => {
      setActiveId(uploaded.id);
      setDocument(uploaded);
      setOutcome(null);
      setError(null);
      setPhase('ready');
    },
    [setActiveId]
  );

  const startDetection = useCallback(async () => {
    if (!document) return;
    setIsStarting(true);
    setError(null);
    try {
      await documentsApi.startDetection(document.id);
    } catch (err) {
      const status = apiErrorStatus(err);
      if (status === 404) {
        expire();
        return;
      }
      // 409: an earlier run is still going — nothing to start, the progress view shows it.
      if (status !== 409) {
        setError(apiErrorMessage(err, t('documents.card.errors.startFailed')));
        return;
      }
    } finally {
      setIsStarting(false);
    }
    setOutcome(null);
    setPhase('detecting');
  }, [document, expire, t]);

  const discard = useCallback(async () => {
    if (!document) return;
    setIsDiscarding(true);
    setError(null);
    try {
      await documentsApi.delete(document.id);
    } catch (err) {
      // A 404 means the document is already gone, which is the outcome we wanted.
      if (apiErrorStatus(err) !== 404) {
        setError(apiErrorMessage(err, t('documents.card.errors.discardFailed')));
        setIsDiscarding(false);
        return;
      }
    }
    setActiveId(null);
    setDocument(null);
    setOutcome(null);
    setIsDiscarding(false);
    setPhase('idle');
  }, [document, setActiveId, t]);

  return {
    phase,
    document,
    detection: polling.status,
    pollingError: polling.error,
    outcome,
    error,
    isStarting,
    isDiscarding,
    onUploaded,
    startDetection,
    discard,
  };
};
